import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/login', { email, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      {/* Left panel */}
      <div style={s.left}>
        <div style={s.leftInner}>
          <div style={s.logo}>
            <img src={require('../assets/logo.png')} alt="Company Logo" style={s.logoImg} />
            <span style={s.logoText}>ECG RHYTHM EXPERTS</span>
          </div>
          <div style={s.leftContent}>
            <h1 style={s.heroTitle}>Holter Analysis<br /><em style={s.heroItalic}>made effortless</em></h1>
            <p style={s.heroDesc}>A unified platform for holter patch distribution, ECG data collection, and cardiac report delivery.</p>
            <div style={s.features}>
              {['Secure ECG data upload', 'Real-time case tracking', 'Instant report delivery'].map((f, i) => (
                <div key={i} style={s.feature}>
                  <div style={s.featureDot} />
                  <span style={s.featureText}>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={s.statsRow}>
            {[['500+', 'Clinics'], ['12K+', 'Reports'], ['99.9%', 'Uptime']].map(([n, l]) => (
              <div key={l} style={s.stat}>
                <div style={s.statNum}>{n}</div>
                <div style={s.statLabel}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={s.right}>
        <div style={s.formWrap} className="animate-fadeUp">
          <div style={s.formHeader}>
            <h2 style={s.formTitle}>Welcome back</h2>
            <p style={s.formSub}>Sign in to your CardioSync account</p>
          </div>

          {error && (
            <div style={s.errorBox}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{flexShrink:0}}>
                <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="#ef4444"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Email address</label>
              <div style={s.inputWrap}>
                <svg style={s.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="3" stroke="#94a3b8" strokeWidth="1.5"/>
                  <path d="m2 7 10 7 10-7" stroke="#94a3b8" strokeWidth="1.5"/>
                </svg>
                <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@clinic.com" required />
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <div style={s.inputWrap}>
                <svg style={s.inputIcon} width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="#94a3b8" strokeWidth="1.5"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="#94a3b8" strokeWidth="1.5"/>
                </svg>
                <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
            </div>
            <button style={{ ...s.btn, ...(loading ? s.btnLoading : {}) }} type="submit" disabled={loading}>
              {loading ? (
                <span style={s.btnInner}>
                  <span style={s.spinner} />
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <p style={s.footNote}>Protected by 256-bit encryption. Your data is safe.</p>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh' },
  left: {
    width: '45%', background: 'linear-gradient(135deg, #0f172a 0%, #0c2340 50%, #0f3460 100%)',
    display: 'flex', flexDirection: 'column', padding: '48px',
    position: 'relative', overflow: 'hidden',
  },
  leftInner: { display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', zIndex: 1 },
  logo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 'auto' },
  logoIcon: {
    width: '40px', height: '40px', borderRadius: '10px',
    background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(14,165,233,0.4)',
  logoImg: { width: '40px', height: '40px', objectFit: 'contain', borderRadius: '8px' },
  },
  logoText: { fontSize: '20px', fontWeight: '600', color: 'white', letterSpacing: '-0.3px' },
  leftContent: { marginBottom: '48px' },
  heroTitle: {
    fontFamily: "'DM Serif Display', serif",
    fontSize: '44px', lineHeight: '1.15', color: 'white',
    marginBottom: '20px', letterSpacing: '-0.5px',
  },
  heroItalic: { color: '#7dd3fc', fontStyle: 'italic' },
  heroDesc: { fontSize: '15px', color: '#94a3b8', lineHeight: '1.7', maxWidth: '340px', marginBottom: '32px' },
  features: { display: 'flex', flexDirection: 'column', gap: '12px' },
  feature: { display: 'flex', alignItems: 'center', gap: '12px' },
  featureDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#0ea5e9', flexShrink: 0 },
  featureText: { fontSize: '14px', color: '#cbd5e1' },
  statsRow: { display: 'flex', gap: '32px', paddingTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  stat: {},
  statNum: { fontSize: '24px', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' },
  statLabel: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
  right: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '48px', background: '#f8fafc',
  },
  formWrap: { width: '100%', maxWidth: '420px' },
  formHeader: { marginBottom: '36px' },
  formTitle: { fontSize: '28px', fontWeight: '600', color: '#0f172a', letterSpacing: '-0.5px', marginBottom: '8px' },
  formSub: { fontSize: '14px', color: '#64748b' },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: '#fff5f5', border: '1px solid #fecaca',
    borderRadius: '10px', padding: '12px 16px',
    fontSize: '13px', color: '#dc2626', marginBottom: '20px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '13px', fontWeight: '500', color: '#374151' },
  inputWrap: { position: 'relative' },
  inputIcon: { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' },
  input: {
    width: '100%', padding: '12px 14px 12px 42px',
    border: '1.5px solid #e2e8f0', borderRadius: '10px',
    fontSize: '14px', color: '#0f172a', background: 'white',
    outline: 'none', transition: 'all 0.2s',
  },
  btn: {
    width: '100%', padding: '13px',
    background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    color: 'white', border: 'none', borderRadius: '10px',
    fontSize: '15px', fontWeight: '600', cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(14,165,233,0.35)',
    transition: 'all 0.2s', marginTop: '4px',
    letterSpacing: '-0.2px',
  },
  btnLoading: { opacity: 0.8, cursor: 'not-allowed' },
  btnInner: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  spinner: {
    width: '16px', height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  footNote: { fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '24px' },
  logoImg: { width: '40px', height: '40px', objectFit: 'contain', borderRadius: '8px' },
};
